import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './RequestedTag.css';
import Layout from '../../../../components/Layout';
import AdminNavbar from '../../../../components/Admin/AdminNavbar/AdminNavbar';
import {
  Autocomplete,
  Box,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
  Input,
  Button,
  FormControl,
  Select,
  MenuItem,
  Popover,
  Chip,
  FormControlLabel,
  Checkbox,
  RadioGroup,
} from '@mui/material';
import PropTypes from 'prop-types';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { axiosInstance } from '../../../../utils/tokenUtils';
import FilterIcon from '../../../../assets/Admin/RequestImages/filter-icon.svg';
import ColoredFilterIcon from '../../../../assets/Admin/RequestImages/colored-filter-icon.svg';
import { TableSkeleton } from '../../../../components/Admin/SkeletonLoader/SkeletonLoader';
import RequestedTagTable from '../../../../components/Admin/Request/RequestedTag/RequestedTagTable/RequestedTagTable';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      component="div"
      role="tabpanel"
      hidden={value !== index}
      id={`tag-tabpanel-${index}`}
      aria-labelledby={`tag-tab-${index}`}
      sx={{
        flex: 1,
        display: value !== index ? 'none' : 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `tag-tab-${index}`,
    'aria-controls': `tag-tabpanel-${index}`,
  };
}

const StatusArray = [
  { value: 'all', label: 'All' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

// Define a constant for the "All" option that will be added to each subject list
const ALL_OPTION = { name: 'All', value: 'all' };

// Utility function to format text
const formatText = text => {
  if (!text) return text;
  return text
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const RequestedTag = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [academicTagData, setAcademicTagData] = useState([]);
  const [creativeTagData, setCreativeTagData] = useState([]);
  const [openFilter, setOpenFilter] = useState(false);
  const [status, setStatus] = useState('all');
  const [subject, setSubject] = useState('all');
  // Pending filter values that are only committed on Apply
  const [pendingStatus, setPendingStatus] = useState('all');
  const [pendingSubject, setPendingSubject] = useState('all');
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Add pagination state variables
  const [academicPage, setAcademicPage] = useState(1);
  const [creativePage, setCreativePage] = useState(1);
  const [academicPageSize, setAcademicPageSize] = useState(10);
  const [creativePageSize, setCreativePageSize] = useState(10);
  const [academicTotalCount, setAcademicTotalCount] = useState(0);
  const [creativeTotalCount, setCreativeTotalCount] = useState(0);

  // Add state variables for subject lists
  const [academicSubjects, setAcademicSubjects] = useState([ALL_OPTION]);
  const [creativeSubjects, setCreativeSubjects] = useState([ALL_OPTION]);
  const [fetchingSubjects, setFetchingSubjects] = useState(false);

  // Popover anchor element
  const [anchorEl, setAnchorEl] = useState(null);

  // Use a ref to track if initial data has been loaded
  const initialDataLoaded = useRef(false);
  // Track if filters-effect has run once to avoid an extra fetch right after initial load
  const hasMountedRef = useRef(false);

  // Get the current subject list based on the active tab - moved after state declarations
  const currentSubjectList = useMemo(() => {
    return tabValue === 0 ? academicSubjects : creativeSubjects;
  }, [tabValue, academicSubjects, creativeSubjects]);

  // Get current pagination state based on active tab
  const getCurrentPaginationState = useCallback(() => {
    if (tabValue === 0) {
      return {
        page: academicPage,
        pageSize: academicPageSize,
        setPage: setAcademicPage,
        setPageSize: setAcademicPageSize,
        totalCount: academicTotalCount,
        setTotalCount: setAcademicTotalCount,
      };
    } else {
      return {
        page: creativePage,
        pageSize: creativePageSize,
        setPage: setCreativePage,
        setPageSize: setCreativePageSize,
        totalCount: creativeTotalCount,
        setTotalCount: setCreativeTotalCount,
      };
    }
  }, [
    tabValue,
    academicPage,
    academicPageSize,
    academicTotalCount,
    creativePage,
    creativePageSize,
    creativeTotalCount,
  ]);

  // Function to fetch subjects based on subject type
  const fetchSubjects = useCallback(async subjectType => {
    try {
      setFetchingSubjects(true);
      const { data } = await axiosInstance.get(`/common/subjects?subject_type=${subjectType}`);

      // Transform the data to match our expected format
      const formattedSubjects = [
        ALL_OPTION,
        ...data.results.map(subject => ({
          name: subject.title,
          value: subject.title.toLowerCase(),
          id: subject.id, // Store ID for future use if needed
        })),
      ];

      if (subjectType === 'academic') {
        setAcademicSubjects(formattedSubjects);
      } else {
        setCreativeSubjects(formattedSubjects);
      }
    } catch (error) {
      console.error(`Error fetching ${subjectType} subjects:`, error);
      // Set default value in case of error
      if (subjectType === 'academic') {
        setAcademicSubjects([ALL_OPTION]);
      } else {
        setCreativeSubjects([ALL_OPTION]);
      }
    } finally {
      setFetchingSubjects(false);
    }
  }, []);

  // Function to create the params for the API call
  const createParams = useCallback(
    (page, pageSize) => {
      const params = new URLSearchParams();

      if (status !== 'all') {
        params.append('status', status);
      }

      // Add subject_type based on current tab
      params.append('subject_type', tabValue === 0 ? 'academic' : 'creative');

      // Add subject_name if a subject is selected
      if (subject !== 'all') {
        // Find the subject title from the current list
        const selectedSubject = currentSubjectList.find(item => item.value === subject);
        if (selectedSubject) {
          params.append('subject_name', selectedSubject.name);
        }
      }

      if (searchTerm) {
        params.append('search_query', searchTerm);
      }

      // Add pagination parameters
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());

      return params;
    },
    [status, subject, searchTerm, tabValue, currentSubjectList]
  );

  // Function to fetch data for a specific tag type
  const fetchTagData = useCallback(
    async (tagType, page, pageSize) => {
      try {
        setLoading(true);

        const params = createParams(page, pageSize);
        const url = `/admin/tags/requests${params.toString() ? `?${params.toString()}` : ''}`;

        const { data } = await axiosInstance.get(url);

        if (tagType === 'academic') {
          setAcademicTagData(data.results || []);
          setAcademicTotalCount(data.count || 0);
        } else {
          setCreativeTagData(data.results || []);
          setCreativeTotalCount(data.count || 0);
        }
      } catch (error) {
        console.error(`Error fetching ${tagType} tag data:`, error);
        if (tagType === 'academic') {
          setAcademicTagData([]);
          setAcademicTotalCount(0);
        } else {
          setCreativeTagData([]);
          setCreativeTotalCount(0);
        }
      } finally {
        setLoading(false);
      }
    },
    [createParams]
  );

  // Initial data load: fetch only the current tab and avoid duplicate calls (set ref before awaiting)
  useEffect(() => {
    if (!initialDataLoaded.current) {
      initialDataLoaded.current = true;
      const currentType = tabValue === 0 ? 'academic' : 'creative';
      const pageSize = tabValue === 0 ? academicPageSize : creativePageSize;
      fetchTagData(currentType, 1, pageSize);
    }
  }, [fetchTagData, tabValue, academicPageSize, creativePageSize]);

  // Effect to handle filter changes
  useEffect(() => {
    // Skip the first run to prevent double-fetch after initial load
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    const currentType = tabValue === 0 ? 'academic' : 'creative';
    const pageSize = tabValue === 0 ? academicPageSize : creativePageSize;
    // Reset to page 1 when filters change
    if (tabValue === 0) {
      setAcademicPage(1);
    } else {
      setCreativePage(1);
    }
    fetchTagData(currentType, 1, pageSize);
  }, [tabValue, status, subject, searchTerm, academicPageSize, creativePageSize, fetchTagData]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Reset filters when changing tabs
    setSubject('all');
    setPendingSubject('all');
    // Do not fetch here; the filters effect will trigger on tabValue change
  };

  // Pagination handlers
  const handlePageChange = newPage => {
    const currentType = tabValue === 0 ? 'academic' : 'creative';
    const paginationState = getCurrentPaginationState();

    if (tabValue === 0) {
      setAcademicPage(newPage);
      fetchTagData(currentType, newPage, paginationState.pageSize);
    } else {
      setCreativePage(newPage);
      fetchTagData(currentType, newPage, paginationState.pageSize);
    }
  };

  const handlePageSizeChange = newPageSize => {
    const currentType = tabValue === 0 ? 'academic' : 'creative';

    if (tabValue === 0) {
      setAcademicPageSize(newPageSize);
      setAcademicPage(1); // Reset to first page
      fetchTagData(currentType, 1, newPageSize);
    } else {
      setCreativePageSize(newPageSize);
      setCreativePage(1); // Reset to first page
      fetchTagData(currentType, 1, newPageSize);
    }
  };

  const handlePendingSubjectChange = event => {
    const selectedValue = event.target.value;
    setPendingSubject(selectedValue);
  };

  // Event Handlers
  const handleClickToOpenFilter = event => {
    setAnchorEl(event.currentTarget);
    // Initialize pending values from applied filters when opening
    setPendingStatus(status);
    setPendingSubject(subject);
    // Lazy-load subjects list for the active tab only once
    const subjectType = tabValue === 0 ? 'academic' : 'creative';
    const needAcademic = subjectType === 'academic' && (academicSubjects?.length || 0) <= 1;
    const needCreative = subjectType === 'creative' && (creativeSubjects?.length || 0) <= 1;
    if (needAcademic) {
      fetchSubjects('academic');
    } else if (needCreative) {
      fetchSubjects('creative');
    }
    setOpenFilter(true);
  };

  const handleApplyFilter = () => {
    // Commit pending values to applied filters
    setStatus(pendingStatus);
    setSubject(pendingSubject);
    setOpenFilter(false);
    setAnchorEl(null);
  };

  const handleCloseFilter = () => {
    setAnchorEl(null);
    setOpenFilter(false);
  };

  const handleChangeSearch = e => {
    setSearchTerm(e.target.value);
  };

  // Clear all filters
  const handleClearAll = () => {
    // Only reset pending values inside the modal; apply happens on Apply
    setPendingStatus('all');
    setPendingSubject('all');
  };

  // Get current data and pagination state for the active tab
  const getCurrentTableProps = () => {
    if (tabValue === 0) {
      return {
        tagData: academicTagData,
        currentPage: academicPage,
        pageSize: academicPageSize,
        totalCount: academicTotalCount,
        onPageChange: handlePageChange,
        onPageSizeChange: handlePageSizeChange,
      };
    } else {
      return {
        tagData: creativeTagData,
        currentPage: creativePage,
        pageSize: creativePageSize,
        totalCount: creativeTotalCount,
        onPageChange: handlePageChange,
        onPageSizeChange: handlePageSizeChange,
      };
    }
  };

  return (
    <Layout title="Klass Online | Requested Tags">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <AdminNavbar />
        </Box>
        <Box className="h-[calc(100vh-64px)] relative bg-[#F9F9F9] rounded-lg mr-[10px] flex flex-col overflow-y-auto">
          <Box className="flex flex-col p-[1rem] gap-3">
            <Box className="flex justify-between items-center">
              <Typography className="requested-tag-page-title">Tag Request</Typography>
              <Box className="flex items-center gap-4 w-[40%]">
                <Input
                  type="search"
                  placeholder="Search by tag name"
                  onChange={handleChangeSearch}
                  value={searchTerm}
                  startAdornment={<SearchIcon className="requested-tag-search-icon-css" />}
                  sx={{
                    width: '100%',
                    height: '40px',
                    background: '#FFF',
                    borderRadius: '56px',
                    padding: '8px 10px',
                    border: '1px solid var(--Stroke, #D5DEE3)',
                    '&:before, &:after': {
                      borderBottom: 'none !important',
                    },
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: 'none !important',
                    },
                    '& .MuiInputBase-input': {
                      padding: '3px 5px 3px 5px',
                    },
                  }}
                />
                {openFilter ? (
                  <img
                    src={ColoredFilterIcon}
                    alt="filter"
                    onClick={handleCloseFilter}
                    style={{ width: 25, height: 25, cursor: 'pointer' }}
                  />
                ) : (
                  <img
                    src={FilterIcon}
                    alt="filter"
                    onClick={handleClickToOpenFilter}
                    style={{ width: 25, height: 25, cursor: 'pointer' }}
                  />
                )}
              </Box>
            </Box>
            <Box className="flex flex-col flex-grow h-full overflow-hidden">
              {/* Filter Modal */}
              <Popover
                open={openFilter}
                anchorEl={anchorEl}
                onClose={handleCloseFilter}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  className: 'requested-tag-filter-modal-container',
                }}
              >
                {/* Header */}
                <Box className="requested-tag-filter-modal-header">
                  <Typography className="requested-tag-filter-modal-title">Filters</Typography>
                </Box>

                {/* Scrollable Content */}
                <Box className="requested-tag-filter-modal-content">
                  {/* Grid for filter items */}
                  <Box className="requested-tag-filter-grid">
                    {/* Status Filter */}
                    <Box className="requested-tag-filter-item">
                      <label className="requested-tag-filter-label">Status</label>
                      <FormControl sx={{ width: '100%' }}>
                        <Select
                          value={pendingStatus}
                          onChange={e => setPendingStatus(e.target.value)}
                          displayEmpty
                          className="requested-tag-filter-input"
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                zIndex: 1500,
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          {StatusArray.map(statusItem => (
                            <MenuItem
                              key={statusItem.value}
                              value={statusItem.value}
                              className="requested-tag-dropdown-option"
                            >
                              {statusItem.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Subject Filter */}
                    <Box className="requested-tag-filter-item">
                      <label className="requested-tag-filter-label">Subject</label>
                      <FormControl sx={{ width: '100%' }}>
                        <Select
                          value={pendingSubject}
                          onChange={handlePendingSubjectChange}
                          displayEmpty
                          className="requested-tag-filter-input"
                          IconComponent={KeyboardArrowDownIcon}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                zIndex: 1500,
                                maxHeight: 200,
                              },
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          }}
                        >
                          {currentSubjectList.map(subjectItem => (
                            <MenuItem
                              key={subjectItem.value}
                              value={subjectItem.value}
                              className="requested-tag-dropdown-option"
                            >
                              {formatText(subjectItem.name)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                </Box>

                {/* Buttons (Sticky Footer) */}
                <Box className="requested-tag-filter-buttons-container">
                  <Button
                    variant="outlined"
                    onClick={handleClearAll}
                    className="requested-tag-filter-clear-button"
                  >
                    Clear All
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleApplyFilter}
                    className="requested-tag-filter-apply-button"
                  >
                    Apply
                  </Button>
                </Box>
              </Popover>

              <Box className="requested-tag-request-table-container flex-grow h-full flex flex-col overflow-hidden">
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    height: '100%',
                  }}
                >
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', background: '#FFF' }}>
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="request tabs">
                      <Tab
                        label="Academic"
                        aria-label="academic instructor tab"
                        sx={{ textTransform: 'none' }}
                        {...a11yProps(0)}
                      />
                      <Tab
                        label="Creative Art"
                        aria-label="creative art instructor tab"
                        sx={{ textTransform: 'none' }}
                        {...a11yProps(1)}
                      />
                    </Tabs>
                  </Box>

                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <TableSkeleton />
                    </Box>
                  ) : (
                    <Box className="flex-grow h-full overflow-hidden" sx={{ mt: 2 }}>
                      <CustomTabPanel value={tabValue} index={0}>
                        <RequestedTagTable {...getCurrentTableProps()} />
                      </CustomTabPanel>
                      <CustomTabPanel value={tabValue} index={1}>
                        <RequestedTagTable {...getCurrentTableProps()} />
                      </CustomTabPanel>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default RequestedTag;
